import { jsPDF } from 'jspdf';

// ===============================
//   COLORES PDF
// ===============================
const PDF_COLORS = {
    primary: [15, 23, 42],
    secondary: [30, 41, 59],
    accent: [6, 182, 212],
    light: [248, 250, 252],
    border: [226, 232, 240],
    text: [15, 23, 42],
};

// ===============================
//   CONFIGURACIÓN PDF
// ===============================
const PDF_CONFIG = {
    pageWidth: 210,
    margin: 20,
    footerY: 285
};

// ===============================
//   FORMATO DE FECHA
// ===============================
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
        if (dateString.includes('/')) return dateString;

        const [year, month, day] = dateString.split('-');

        const date = new Date(
            Number(year),
            Number(month) - 1,
            Number(day)
        );

        return date.toLocaleDateString('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });

    } catch (error) {
        console.error('Error formateando fecha:', error);
        return 'N/A';
    }
};

// ===============================
//   CONTROL DE SALTO DE PÁGINA
// ===============================
const checkPageBreak = (pdf, currentY, neededSpace = 10) => {
    if (currentY > 270 - neededSpace) {
        pdf.addPage();
        return 20;
    }
    return currentY;
};

// ===============================
//   ENCABEZADO DEL DOCUMENTO
// ===============================
const drawHeader = (pdf) => {
    const { pageWidth } = PDF_CONFIG;

    pdf.setFillColor(...PDF_COLORS.primary);
    pdf.rect(0, 0, pageWidth, 45, 'F');

    pdf.setFillColor(...PDF_COLORS.accent);
    pdf.rect(0, 0, pageWidth, 3, 'F');

    pdf.setFontSize(20);
    pdf.setTextColor(...PDF_COLORS.light);
    pdf.setFont('helvetica', 'bold');
    pdf.text('ELEKTRONIKS', pageWidth / 2, 18, { align: 'center' });

    pdf.setFontSize(10);
    pdf.setTextColor(...PDF_COLORS.accent);
    pdf.setFont('helvetica', 'normal');
    pdf.text(
        'SERVICIO DE MANTENIMIENTO Y REPARACIÓN DE CAMIONES PESADOS',
        pageWidth / 2,
        25,
        { align: 'center' }
    );

    pdf.setFontSize(8);
    pdf.setTextColor(180, 180, 180);
    pdf.text(
        'Av. de los Maestros 2206 • Aguascalientes, Ags • C.P. 20235',
        pageWidth / 2,
        32,
        { align: 'center' }
    );

    pdf.text(
        'Cel: 449 205 1854 • elektroniks-@outlook.com',
        pageWidth / 2,
        36,
        { align: 'center' }
    );
};

// ===============================
//   SECCIÓN CON TÍTULO
// ===============================
const drawSection = (pdf, title, y, contentCallback) => {
    const { margin, pageWidth } = PDF_CONFIG;
    const contentWidth = pageWidth - (2 * margin);

    pdf.setFillColor(...PDF_COLORS.secondary);
    pdf.rect(margin, y, contentWidth, 7, 'F');

    pdf.setFontSize(10);
    pdf.setTextColor(...PDF_COLORS.light);
    pdf.setFont('helvetica', 'bold');
    pdf.text(title, margin + 5, y + 4.5);

    let newY = y + 10;
    if (contentCallback) newY = contentCallback(pdf, newY);

    return newY;
};

// ===============================
//   TABLA DE REFACCIONES
// ===============================
const drawPartsTable = (pdf, parts, startY) => {
    const { margin, pageWidth } = PDF_CONFIG;
    const contentWidth = pageWidth - (2 * margin);

    let currentY = startY;

    const cantidadWidth = 25;
    const descripcionWidth = contentWidth - cantidadWidth - 10;

    // Punto de alineación único para descripción
    const descX = margin + cantidadWidth + 10;

    // Encabezado tabla
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, currentY, contentWidth, 6, 'F');

    pdf.setFontSize(8);
    pdf.setTextColor(...PDF_COLORS.text);
    pdf.setFont('helvetica', 'bold');

    // Cantidad (centrado)
    const cantidadHeader = 'CANTIDAD';
    const cantidadHeaderWidth = pdf.getTextWidth(cantidadHeader);
    const cantidadX = margin + (cantidadWidth - cantidadHeaderWidth) / 2;
    pdf.text(cantidadHeader, cantidadX, currentY + 4);

    // Descripción
    pdf.text('DESCRIPCIÓN', descX, currentY + 4);

    currentY += 7;

    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');

    const lineHeight = 3.5;

    parts.forEach((part) => {
        const description = part.name || part.description || 'Sin descripción';
        const descriptionLines = pdf.splitTextToSize(
            description,
            descripcionWidth - 15
        );

        // Altura real del bloque de texto
        const textBlockHeight = descriptionLines.length * lineHeight;

        // Altura del renglón
        const rowHeight = Math.max(8, textBlockHeight + 1);

        // Checar salto de página con el alto real del renglón
        currentY = checkPageBreak(pdf, currentY, rowHeight + 5);

        // =========================
        //  CENTRADO VERTICAL
        // =========================

        // Cantidad centrada verticalmente
        const quantityText = `${part.quantity}`;
        const quantityX = margin + cantidadWidth / 2;
        const qtyY = currentY + rowHeight / 2;

        pdf.text(quantityText, quantityX, qtyY, {
            align: 'center',
            baseline: 'middle'
        });

        // Descripción centrada como bloque
        const blockStartY = currentY + (rowHeight - textBlockHeight) / 2;

        descriptionLines.forEach((line, i) => {
            const lineY = blockStartY + i * lineHeight + lineHeight / 2;

            pdf.text(line, descX, lineY, {
                baseline: 'middle'
            });
        });

        // Línea separadora
        pdf.setDrawColor(...PDF_COLORS.border);
        pdf.line(
            margin,
            currentY + rowHeight,
            margin + contentWidth,
            currentY + rowHeight
        );

        currentY += rowHeight;
    });

    return currentY + 2;
};

// ===============================
//   INFORMACIÓN DEL VEHÍCULO
// ===============================
const drawVehicleInfo = (pdf, data, y) => {
    const { margin } = PDF_CONFIG;

    pdf.setFontSize(9);
    pdf.setTextColor(...PDF_COLORS.text);

    let currentY = y + 5;

    pdf.setFont('helvetica', 'bold');
    pdf.text('CLIENTE:', margin + 5, currentY);
    pdf.text('UNIDAD:', margin + 5, currentY + 5);
    pdf.text('MOTOR:', margin + 5, currentY + 10);
    pdf.text('MODELO:', margin + 5, currentY + 15);

    pdf.text('VIN:', margin + 90, currentY);
    pdf.text('PLACAS:', margin + 90, currentY + 5);
    pdf.text('KILOMETRAJE:', margin + 90, currentY + 10);

    pdf.setFont('helvetica', 'normal');
    pdf.text(data.client || 'N/A', margin + 25, currentY);
    pdf.text(data.unit || 'N/A', margin + 25, currentY + 5);
    pdf.text(data.engine || 'N/A', margin + 25, currentY + 10);
    pdf.text(data.model || 'N/A', margin + 25, currentY + 15);

    pdf.text(data.vin || 'N/A', margin + 115, currentY);
    pdf.text(data.plates || 'N/A', margin + 115, currentY + 5);
    pdf.text((data.mileage || 'N/A') + ' km', margin + 115, currentY + 10);

    return currentY + 22;
};

// ===============================
//   REPORTE DEL CHOFER
// ===============================
const drawDriverReport = (pdf, data, y) => {
    const { margin, pageWidth } = PDF_CONFIG;
    const contentWidth = pageWidth - (2 * margin);

    pdf.setFontSize(9);
    pdf.setTextColor(...PDF_COLORS.text);

    let currentY = y + 5;

    if (data.driverName) {
        // Nombre del chofer
        pdf.setFont('helvetica', 'bold');
        pdf.text('NOMBRE DEL CHOFER:', margin + 5, currentY);

        pdf.setFont('helvetica', 'normal');
        pdf.text(data.driverName, margin + 45, currentY);

        currentY += 5;

        // DESCRIPCIÓN EN LA MISMA LÍNEA
        const label = 'DESCRIPCIÓN:';
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, margin + 5, currentY);

        const labelWidth = pdf.getTextWidth(label + ' ');
        const descX = margin + 5 + labelWidth + 1; // espacio después de la etiqueta

        const desc = data.driverDescription || 'Sin descripción';

        pdf.setFont('helvetica', 'normal');

        const maxWidth = contentWidth - (descX - margin) - 5;
        const lines = pdf.splitTextToSize(desc, maxWidth);
        const lineHeight = 3.5;

        lines.forEach((line, index) => {
            const x = index === 0 ? descX : margin + 5;
            if (index > 0) currentY += lineHeight;

            currentY = checkPageBreak(pdf, currentY, 10);
            pdf.text(line, x, currentY);
        });

        currentY += 5;
    }

    return currentY;
};

// ===============================
//   SERVICIOS
// ===============================
const drawServices = (pdf, data, y) => {
    const { margin, pageWidth } = PDF_CONFIG;
    const contentWidth = pageWidth - (2 * margin);

    pdf.setFontSize(9);
    pdf.setTextColor(...PDF_COLORS.text);

    let currentY = y + 5;

    // Tipo de diagnóstico
    pdf.setFont('helvetica', 'bold');
    pdf.text('TIPO DE DIAGNÓSTICO:', margin + 5, currentY);

    pdf.setFont('helvetica', 'normal');
    pdf.text(data.diagnosticType || 'N/A', margin + 45, currentY);

    currentY += 5;

    // DESCRIPCIÓN EN LA MISMA LÍNEA
    const label = 'DESCRIPCIÓN:';
    pdf.setFont('helvetica', 'bold');
    pdf.text(label, margin + 5, currentY);

    const labelWidth = pdf.getTextWidth(label + ' ');
    const descX = margin + 5 + labelWidth + 1;

    const techDesc = data.technicalDescription || 'No se realizó descripción técnica';

    pdf.setFont('helvetica', 'normal');

    const maxWidth = contentWidth - (descX - margin) - 5;
    const techLines = pdf.splitTextToSize(techDesc, maxWidth);
    const lineHeight = 3.5;

    techLines.forEach((line, index) => {
        const x = index === 0 ? descX : margin + 5;
        if (index > 0) currentY += lineHeight;

        currentY = checkPageBreak(pdf, currentY, 10);
        pdf.text(line, x, currentY);
    });

    currentY += 5;

    return currentY;
};

// ===============================
//   OBSERVACIONES
// ===============================
const drawObservations = (pdf, data, y) => {
    const { margin, pageWidth } = PDF_CONFIG;
    const contentWidth = pageWidth - (2 * margin);

    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...PDF_COLORS.text);

    let currentY = y + 5;

    const obs =
        data.additionalObservations ||
        'No se registraron observaciones adicionales';

    const obsLines = pdf.splitTextToSize(obs, contentWidth - 10);
    pdf.text(obsLines, margin + 5, currentY);

    currentY += obsLines.length * 3.5 + 5;

    return currentY;
};

// ===============================
//   PIE DE PÁGINA
// ===============================
const drawFooter = (pdf) => {
    const { margin, pageWidth, footerY } = PDF_CONFIG;
    const contentWidth = pageWidth - (2 * margin);

    pdf.setDrawColor(...PDF_COLORS.border);
    pdf.line(margin, footerY - 5, margin + contentWidth, footerY - 5);

    pdf.setFontSize(7);
    pdf.setTextColor(150, 150, 150);
    pdf.text(
        'Documento generado automáticamente por el Software de ELEKTRONIKS',
        pageWidth / 2,
        footerY,
        { align: 'center' }
    );
};

// ===============================
//   FUNCIÓN PRINCIPAL
// ===============================
export const generateProfessionalPDF = async (workOrderData) => {
    const pdf = new jsPDF();
    const { pageWidth, margin } = PDF_CONFIG;
    const contentWidth = pageWidth - (2 * margin);

    drawHeader(pdf);

    let y = 52;

    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(...PDF_COLORS.text);
    pdf.text('REPORTE DE SERVICIO', margin, y + 5);

    y += 13;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('FOLIO:', margin, y);
    pdf.setFont('helvetica', 'normal');
    pdf.text(workOrderData.workOrderNumber || 'N/A', margin + 15, y);

    pdf.setFont('helvetica', 'bold');
    pdf.text('FECHA:', margin + contentWidth - 40, y);
    pdf.setFont('helvetica', 'normal');

    const formattedDate = formatDate(workOrderData.date);
    pdf.text(formattedDate, margin + contentWidth - 25, y);

    y += 8;

    y = drawSection(pdf, 'INFORMACIÓN', y, (pdf, yy) =>
        drawVehicleInfo(pdf, workOrderData, yy)
    );

    if (workOrderData.driverName || workOrderData.driverDescription) {
        y = checkPageBreak(pdf, y);
        y = drawSection(pdf, 'REPORTE', y, (pdf, yy) =>
            drawDriverReport(pdf, workOrderData, yy)
        );
    }

    y = checkPageBreak(pdf, y);
    y = drawSection(pdf, 'SERVICIOS REALIZADOS', y, (pdf, yy) =>
        drawServices(pdf, workOrderData, yy)
    );

    const allPartsUsed = [
        ...(workOrderData.partsUsed || []),
        ...(workOrderData.manualParts || [])
    ];

    if (allPartsUsed.length > 0) {
        y = checkPageBreak(pdf, y, 15);
        y = drawSection(pdf, 'REFACCIONES UTILIZADAS', y, (pdf, yy) => {
            const startY = yy + 5;
            let endY = drawPartsTable(pdf, allPartsUsed, startY);
            return endY + 5;
        });
    }

    y = checkPageBreak(pdf, y);
    y = drawSection(pdf, 'OBSERVACIONES ADICIONALES', y, (pdf, yy) =>
        drawObservations(pdf, workOrderData, yy)
    );

    drawFooter(pdf);

    const safeDate = formattedDate !== 'N/A'
        ? formattedDate.replace(/\//g, '-')
        : 'sin-fecha';

    const fileName =
        `Reporte_${workOrderData.workOrderNumber || 'sin-folio'}_${safeDate}.pdf`;

    // ✅ "Ver" debe abrir en pestaña (no descargar) cuando hay window.
    if (typeof window !== 'undefined') {
        // jsPDF abre un visor en nueva pestaña/ventana
        pdf.output('dataurlnewwindow');
        return pdf;
    }

    // Fallback (SSR / entorno sin window)
    pdf.save(fileName);

    return pdf;
};

export default generateProfessionalPDF;
